import { memo, useCallback } from 'react';

import { ArchiveIcon, EllipsisIcon, PencilIcon } from '@air/design-icons';
import { DropdownMenu, DropdownMenuItem } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

import { useArchiveWorkspaceAgentTask } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useArchiveWorkspaceAgentTask';
import { usePrivateWorkspaceAgentPanelSessionStatus } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentPanelSessionStatus';
import { usePrivateWorkspaceAgentPanelSession } from '~/components/v2/components/workspace/workspace-agent-panel/PrivateWorkspaceAgentPanelProvider';
import { type WorkspaceAgentPanelView } from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentPanelView';
import { isWorkspaceAgentSessionArchivableFromList } from '~/components/v2/components/workspace/workspace-agent-panel/utils/isWorkspaceAgentSessionArchivableFromList';

export type PrivateWorkspaceAgentPanelTaskHeaderOverflowMenuProps = {
  disabled: boolean;
  onRename: () => void;
  panelView: WorkspaceAgentPanelView;
};

export const PrivateWorkspaceAgentPanelTaskHeaderOverflowMenu = memo(
  ({ disabled, onRename, panelView }: PrivateWorkspaceAgentPanelTaskHeaderOverflowMenuProps) => {
    const session = usePrivateWorkspaceAgentPanelSession();
    const { isIdle } = usePrivateWorkspaceAgentPanelSessionStatus();
    const { archiveTask, isArchivingSession } = useArchiveWorkspaceAgentTask();

    const handlePreventCloseAutoFocus = useCallback((event: Event) => {
      event.preventDefault();
    }, []);

    const handleArchive = useCallback(() => {
      if (!session.sessionId) {
        return;
      }

      void archiveTask(session.sessionId);
    }, [archiveTask, session.sessionId]);

    if (panelView !== 'chat' || session.sessionId === null) {
      return null;
    }

    const activeSession = session.sessions.find((item) => item.id === session.sessionId);
    const showArchive = activeSession?.status !== 'archived';
    const isArchivable =
      activeSession !== undefined && isWorkspaceAgentSessionArchivableFromList(activeSession) && isIdle;

    return (
      <DropdownMenu
        align="end"
        disabled={disabled}
        onCloseAutoFocus={handlePreventCloseAutoFocus}
        sideOffset={4}
        trigger={
          <IconButton
            appearance="ghost"
            color="gray"
            data-testid="WORKSPACE_AGENT_TASK_HEADER_OVERFLOW_MENU_TRIGGER"
            disabled={disabled}
            label="Thread options"
            size="small"
            type="button"
          >
            <EllipsisIcon aria-hidden size="sm" />
          </IconButton>
        }
        triggerTooltip={<span data-testid="WORKSPACE_AGENT_TASK_HEADER_OVERFLOW_MENU_TOOLTIP">Thread options</span>}
      >
        <DropdownMenuItem
          data-testid="WORKSPACE_AGENT_TASK_HEADER_RENAME"
          label="Rename task"
          onSelect={onRename}
          prefix={<PencilIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />}
        />
        {showArchive ? (
          <DropdownMenuItem
            data-testid="WORKSPACE_AGENT_TASK_HEADER_ARCHIVE_TASK"
            disabled={!isArchivable || isArchivingSession(session.sessionId)}
            label="Archive task"
            onSelect={handleArchive}
            prefix={<ArchiveIcon aria-hidden className="ads:text-icon-subtle text-gray-9" size="sm" />}
          />
        ) : null}
      </DropdownMenu>
    );
  },
);

PrivateWorkspaceAgentPanelTaskHeaderOverflowMenu.displayName = 'PrivateWorkspaceAgentPanelTaskHeaderOverflowMenu';
